import type { CopyDeterConfig, MobileBlurType } from '../types';

const VALID_MOBILE_BLUR_TYPES: MobileBlurType[] = ['diagonal', 'horizontal'];

/**
 * Default overlay HTML shown when no custom content is provided
 */
export const DEFAULT_OVERLAY_HTML = `
<div style="text-align: center; padding: 40px;">
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
  </svg>
  <h2 style="margin: 20px 0 10px; font-size: 24px; font-weight: 600;">Screenshot Protection Enabled</h2>
  <p style="margin: 0; font-size: 16px; opacity: 0.8;">This content is protected. Screenshots are not permitted.</p>
</div>
`;

/**
 * Validates the provided configuration and throws descriptive errors for invalid values
 */
export function validateConfig(config: unknown): asserts config is CopyDeterConfig {
    if (!config || typeof config !== 'object') {
        throw new Error('CopyDeter.init() requires a configuration object');
    }

    const cfg = config as Record<string, unknown>;

    // Validate screenshotProtectionEnabled
    if (typeof cfg.screenshotProtectionEnabled !== 'boolean') {
        throw new Error('screenshotProtectionEnabled must be a boolean');
    }

    // Validate mobileProtectionEnabled
    if (typeof cfg.mobileProtectionEnabled !== 'boolean') {
        throw new Error('mobileProtectionEnabled must be a boolean');
    }

    // Validate targetSelectors
    if (!Array.isArray(cfg.targetSelectors)) {
        throw new Error('targetSelectors must be an array of CSS selector strings');
    }

    for (const selector of cfg.targetSelectors) {
        if (typeof selector !== 'string' || selector.trim() === '') {
            throw new Error('Each targetSelector must be a non-empty string');
        }

        // Validate selector syntax by attempting to use it
        try {
            document.querySelector(selector);
        } catch {
            throw new Error(`Invalid CSS selector: "${selector}"`);
        }
    }

    // Validate overlayHtml (optional)
    if (cfg.overlayHtml !== undefined && typeof cfg.overlayHtml !== 'string') {
        throw new Error('overlayHtml must be a string if provided');
    }

    // Validate windowFocusOverlayHtml (optional)
    if (
        cfg.windowFocusOverlayHtml !== undefined &&
        typeof cfg.windowFocusOverlayHtml !== 'string'
    ) {
        throw new Error('windowFocusOverlayHtml must be a string if provided');
    }

    // Validate windowFocusProtectionLevel (optional)
    const validLevels = ['off', 'blur', 'visibilitychange'];

    if (
        cfg.windowFocusProtectionLevel !== undefined &&
        !validLevels.includes(cfg.windowFocusProtectionLevel as string)
    ) {
        throw new Error(`windowFocusProtectionLevel must be one of: ${validLevels.join(', ')}`);
    }

    // Validate mobileBlurType (optional)
    if (
        cfg.mobileBlurType !== undefined &&
        !VALID_MOBILE_BLUR_TYPES.includes(cfg.mobileBlurType as MobileBlurType)
    ) {
        throw new Error(`mobileBlurType must be one of: ${VALID_MOBILE_BLUR_TYPES.join(', ')}`);
    }

    // Validate forceMobile (optional)
    if (cfg.forceMobile !== undefined && typeof cfg.forceMobile !== 'boolean') {
        throw new Error('forceMobile must be a boolean if provided');
    }

    // Validate debug (optional)
    if (cfg.debug !== undefined && typeof cfg.debug !== 'boolean') {
        throw new Error('debug must be a boolean if provided');
    }
}

/**
 * Merges user config with defaults
 */
export function normalizeConfig(config: CopyDeterConfig): Required<CopyDeterConfig> {
    return {
        ...config,
        overlayHtml: config.overlayHtml ?? DEFAULT_OVERLAY_HTML,
        windowFocusOverlayHtml:
            config.windowFocusOverlayHtml ?? config.overlayHtml ?? DEFAULT_OVERLAY_HTML,
        windowFocusProtectionLevel: config.windowFocusProtectionLevel ?? 'off',
        mobileBlurType: config.mobileBlurType ?? 'diagonal',
        forceMobile: config.forceMobile ?? false,
        debug: config.debug ?? false
    };
}
